import { getRequiredFields } from "@/lib/utils";
import { setMyCommunityCurrView, setPropertyData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { getOwnerBomBase, routes } from "@/routes/utils";
import { useGetActiveInsuranceTypes, useGetActivePropertyCategories, useGetActivePropertyModels, useGetActivePropertyZones, useGetPropertyBlockByCommunityZone, useGetPropertyById } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { propertySchema } from "./validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import {
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  CATEGORY,
  INSURANCE_TYPE,
  NO_OF_GARAGES,
  PIN,
  POC_EMAIL,
  POC_MOB_NO,
  POC_NAME,
  PROPERTY_ID,
  PROPERTY_MODEL,
  PROPERTY_SIZE,
  ADDRESS,
  UNIT_NUMBER,
  EMERGENCY_CONTACTS,
  PROPERTY_BLOCK,
  PROPERTY_ZONE,
  COMMUNITY_ID,
  COUNTRY,
  PROPERTY_NAME,
  COMMUNITY_NAME,
  DISPLAY_ID,
} from "./consts";
import { PROPERTIES } from "../../consts";

const useAddEditProperty = () => {
  const isView = true;
  const dispatch = useDispatch();
  const lastInputRef = useRef<HTMLInputElement>(null);
  const [stateKey, setStateKey] = useState<number>(Math.random() * 2);

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const propertyData = useSelector(
    (state: RootState) => state.currentData.propertyData
  );

  const propertyId = propertyData?.propertyId;

  const id = communityData?.communityId;
  const base = getOwnerBomBase(useLocation().pathname);

  const form = useForm<any>({
    defaultValues: {
      [PROPERTY_ID]: propertyId,
      [DISPLAY_ID]: "",
      [PROPERTY_NAME]: "",
      [UNIT_NUMBER]: "",
      [PIN]: "",
      [INSURANCE_TYPE]: undefined,
      [CATEGORY]: undefined,
      [PROPERTY_ZONE]: undefined,
      [PROPERTY_BLOCK]: undefined,
      [PROPERTY_MODEL]: undefined,
      [PROPERTY_SIZE]: "",
      [NO_OF_GARAGES]: undefined,
      // [IS_RENTED]: false,
      [ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: undefined,
        [STATE]: "",
        [CITY]: "",
        [ZIPCODE]: "",
        [COUNTRY]: "USA",
      },
      [EMERGENCY_CONTACTS]: [
        {
          [POC_NAME]: "",
          [POC_MOB_NO]: "",
          [POC_EMAIL]: "",
        },
      ],
      [COMMUNITY_ID]: id?.toString(),
      [COMMUNITY_NAME]: communityData?.communityName || "",
    },
    resolver: zodResolver(propertySchema),
  });
  const [emergeCount, setEmergeCount] = useState<number>(1);
  const [key, setKey] = useState<any>({
    insuranceType: Math.random() * 2,
    propertyBlock: Math.random() * 2,
    propertyCategories: Math.random() * 2,
    propertyModels: Math.random() * 2,
    propertyZones: Math.random() * 2
  });

  const requiredFields = getRequiredFields(propertySchema);

  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );

  const { data: propertyDetails, isLoading: isDetailLoading } =
    useGetPropertyById({
      communityId: communityData?.communityId,
      property_id: propertyId || "",
      queryParams: {
        enabled: !!propertyId,
      },
    });

    const zoneId = useWatch({control: form.control, name: PROPERTY_ZONE});
    
    const { data: insuranceType, isLoading: isInsuranceLoading } =
    useGetActiveInsuranceTypes({communityId: communityData?.communityId});
    const { data: propertyBlock, isLoading: isBlockLoading } =
    useGetPropertyBlockByCommunityZone({
      communityId: communityData?.communityId,
      zoneId: zoneId,
      queryParams: {
        enabled: !!zoneId
      }
    });
  const { data: propertyCategories, isLoading: isCategoryLoading } =
    useGetActivePropertyCategories({communityId: communityData?.communityId});
  const { data: propertyModels, isLoading: isModelLoading } =
    useGetActivePropertyModels({communityId: communityData?.communityId});
  const { data: propertyZones, isLoading: isZoneLoading } =
    useGetActivePropertyZones({communityId: communityData?.communityId});  

  const breadcrumbData = useMemo(() => ([
    {
      link: `/${base}/${routes.communities}`,
      name: `Community - ${communityData?.communityName || ""}`,
    },
    {
      link: `/${base}/${routes.communities}/${id}/${routes.properties}`,
      name: "Manage Property",
    },
    {
      link: "",
      name: (
        <span className="text-primary">
          {`View Property - ${propertyDetails?.data?.name || ""}`}
        </span>
      ),
    },
  ]), [propertyDetails]);

  useEffect(() => {
    if (propertyDetails) {
      dispatch(setPropertyData(propertyDetails?.data));
      const details = propertyDetails?.data;
      form.reset({
        [PROPERTY_ID]: propertyId,
        [DISPLAY_ID]: details?.[DISPLAY_ID]?.toString() || "",
        [PROPERTY_NAME]: details?.[PROPERTY_NAME] || "",
        [UNIT_NUMBER]: details?.[UNIT_NUMBER] || "",
        [PIN]: details?.[PIN] || "",
        [INSURANCE_TYPE]: details?.[INSURANCE_TYPE]?.toString() || undefined,
        [CATEGORY]: details?.[CATEGORY]?.toString() || undefined,
        [PROPERTY_ZONE]: details?.[PROPERTY_ZONE]?.toString() || undefined,
        [PROPERTY_BLOCK]: details?.[PROPERTY_BLOCK]?.toString() || undefined,
        [PROPERTY_MODEL]: details?.[PROPERTY_MODEL]?.toString() || undefined,
        [PROPERTY_SIZE]: details?.[PROPERTY_SIZE] || "",
        [NO_OF_GARAGES]: details?.[NO_OF_GARAGES] || undefined,
        // [IS_RENTED]: details?.[IS_RENTED] || false,
        [ADDRESS]: {
          [ADDRESS_1]: details?.[ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]: details?.[ADDRESS]?.[ADDRESS_2] || undefined,
          [STATE]: stateList?.find((data: any) => data?.name?.toLowerCase() == details?.[ADDRESS]?.[STATE]?.toLowerCase())?.code || "",
          [CITY]: details?.[ADDRESS]?.[CITY] || "",
          [ZIPCODE]: details?.[ADDRESS]?.[ZIPCODE] || "",
          [COUNTRY]: details?.[ADDRESS]?.[COUNTRY] || "USA",
        },
        [EMERGENCY_CONTACTS]: details?.[EMERGENCY_CONTACTS] || [
          {
            [POC_NAME]: "",
            [POC_MOB_NO]: "",
            [POC_EMAIL]: "",
          },
        ],
        [COMMUNITY_ID]: id?.toString(),
        [COMMUNITY_NAME]: communityData?.communityName || ""
      })
      setStateKey(Math.random() * 3);
      setEmergeCount(details?.[EMERGENCY_CONTACTS]?.length || 1);
      setKey({
        insuranceType: Math.random() * 2,
        propertyBlock: Math.random() * 2,
        propertyCategories: Math.random() * 2,
        propertyModels: Math.random() * 2,
        propertyZones: Math.random() * 2,
      });
    }
  }, [propertyDetails]);

  const onBackClick = () => {
    dispatch(setMyCommunityCurrView(PROPERTIES));
  };

  useEffect(() => {
    if (insuranceType) {
      setKey((prev: any) => ({ ...prev, insuranceType: Math.random() * 4 }));
    }
  }, [insuranceType]);

  useEffect(() => {
    if (propertyBlock) {
      setKey((prev: any) => ({ ...prev, propertyBlock: Math.random() * 4 }));
    }
  }, [propertyBlock]);

  useEffect(() => {
    if (propertyCategories) {
      setKey((prev: any) => ({
        ...prev,
        propertyCategories: Math.random() * 4,
      }));
    }
  }, [propertyCategories]);

  useEffect(() => {
    if (propertyModels) {
      setKey((prev: any) => ({ ...prev, propertyModels: Math.random() * 4 }));
    }
  }, [propertyModels]);
  useEffect(() => {
    if (propertyZones) {
      setKey((prev: any) => ({ ...prev, propertyZones: Math.random() * 4 }));
    }
  }, [propertyZones]);
  
  const propertyZoneValue = useWatch({control: form.control, name: PROPERTY_ZONE});
  const propertyBlockValue = useWatch({control: form.control, name: PROPERTY_BLOCK});

  const zoneName = useMemo(() => propertyZones?.data?.find((data: any) => data?.zoneId?.toString() == propertyZoneValue?.toString())?.zoneName || "", [propertyZoneValue, propertyZones, propertyDetails]);
  const blockName = useMemo(() => propertyBlock?.data?.find((data: any) => data?.blockId?.toString() == propertyBlockValue?.toString())?.blockName || "", [propertyBlockValue, propertyBlock, propertyDetails]);


  const displayHierarchy = useMemo(() => {
    return zoneName && blockName ? `${communityData?.communityName} ${zoneName ? ` - ${zoneName}` : ''} ${blockName ? `- ${blockName}` : ''}` : '-'
  }, [zoneName, blockName]);

  const generatedId = useMemo(() => {
    const id = zoneName && blockName ? `${communityData?.communityName} ${zoneName ? ` - ${zoneName}` : ''} ${blockName ? `- ${blockName}` : ''} ${propertyDetails?.data?.displayId ? `- ${propertyDetails?.data?.displayId}` : ''}` : '-';
    // form.setValue(DISPLAY_ID, id);
    return id
  }, [zoneName, blockName, propertyDetails]);

  return {
    onBackClick,
    isView,
    propertyId,
    isDetailLoading,
    breadcrumbData,
    form,
    requiredFields,
    emergeCount,
    setEmergeCount,
    stateList,
    lastInputRef,
    insuranceType,
    propertyBlock,
    propertyCategories,
    propertyModels,
    propertyZones,
    isInsuranceLoading,
    isBlockLoading,
    isCategoryLoading,
    isModelLoading,
    isZoneLoading,
    key,
    displayHierarchy,
    zoneName,
    blockName,
    onCancel: onBackClick,
    generatedId,
    stateKey
  };
};

export default useAddEditProperty;
